import {useCallback, useState} from 'react'

import {useSuiContext} from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {NOTIFICATION_TYPES} from '@adv-mt/mt-notifications-ads-comparator'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

/**
 * Custom hook to manage comparator ads functionality
 * Handles: selection, removal, clearing, tracking, and notifications
 */
const useComparator = ({origin, pageName, extendedTrackingData = {}, autoLoad = true}) => {
  const {domain} = useSuiContext()
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')
  const {
    HIT_ORIGINS: {LIST_ADS_COMPARISON_MENU},
    TRACKING_EVENT_NAMES: {AD_TO_COMPARE_ADDED, AD_TO_COMPARE_REMOVED}
  } = config.get('tracking')

  const [comparatorSelectedAds, setComparatorSelectedAds] = useState([])
  const [comparatorNotificationType, setComparatorNotificationType] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  /**
   * Track ad comparison action
   */
  const trackAdToCompare = useCallback(
    async ({ad, hasAddedToCompare, trackingOrigin}) => {
      const adComparatorTrackingData = await domain.get('get_event_tracking_data_use_case').execute({
        ad: {...ad, list_position: String(ad.list_position)},
        origin: trackingOrigin || origin,
        listType: extendedTrackingData?.products?.[0]?.list_type,
        context: pageName,
        event: hasAddedToCompare ? AD_TO_COMPARE_ADDED : AD_TO_COMPARE_REMOVED,
        extendedTrackingData
      })
      track(adComparatorTrackingData)
    },
    [domain, origin, pageName, extendedTrackingData, track, AD_TO_COMPARE_ADDED, AD_TO_COMPARE_REMOVED]
  )

  /**
   * Load comparator ads from storage
   */
  const loadComparatorAds = useCallback(async () => {
    setIsLoading(true)
    const [error, response] = await domain.get('get_comparator_ads_use_case').execute()

    if (!error) {
      setComparatorSelectedAds(response?.ads || [])
    }
    setIsLoading(false)
    return [error, response]
  }, [domain])

  /**
   * Handle ad selection/deselection for comparison
   */
  const handleSelectComparar = useCallback(
    async ad => {
      const isAlreadySelected = comparatorSelectedAds.some(selectedAd => Number(selectedAd.id) === Number(ad.id))

      if (!isAlreadySelected && comparatorSelectedAds.length >= 3) {
        setComparatorNotificationType(NOTIFICATION_TYPES.MAX_ADDED)
        return
      }

      setComparatorNotificationType(isAlreadySelected ? NOTIFICATION_TYPES.REMOVED : NOTIFICATION_TYPES.ADDED)
      setComparatorSelectedAds(prevSelected => {
        if (isAlreadySelected) {
          return prevSelected.filter(selectedAd => Number(selectedAd.id) !== Number(ad.id))
        }
        return [...prevSelected, ad]
      })

      if (isAlreadySelected) {
        await domain.get('remove_comparator_ads_use_case').execute({adId: ad.id})
      } else {
        await domain.get('add_comparator_ads_use_case').execute({ad})
      }

      trackAdToCompare({ad, hasAddedToCompare: !isAlreadySelected})
    },
    [comparatorSelectedAds, domain, trackAdToCompare]
  )

  /**
   * Remove a specific ad from comparison
   */
  const onRemoveSelectedAd = useCallback(
    async ad => {
      setComparatorSelectedAds(prevSelected => {
        return prevSelected.filter(selectedAd => Number(selectedAd.id) !== Number(ad.id))
      })
      await domain.get('remove_comparator_ads_use_case').execute({adId: ad.id})

      setComparatorNotificationType(NOTIFICATION_TYPES.REMOVED)
      trackAdToCompare({ad, hasAddedToCompare: false, trackingOrigin: LIST_ADS_COMPARISON_MENU})
    },
    [domain, trackAdToCompare, LIST_ADS_COMPARISON_MENU]
  )

  /**
   * Clear all selected ads from comparison
   */
  const clearSelectedAds = useCallback(async () => {
    const previousComparatorSelectedAds = comparatorSelectedAds

    setComparatorNotificationType(NOTIFICATION_TYPES.REMOVED_ALL)
    setComparatorSelectedAds([])
    await domain.get('clear_comparator_ads_use_case').execute()

    previousComparatorSelectedAds.forEach(ad =>
      trackAdToCompare({ad, hasAddedToCompare: false, trackingOrigin: LIST_ADS_COMPARISON_MENU})
    )
  }, [comparatorSelectedAds, domain, trackAdToCompare, LIST_ADS_COMPARISON_MENU])

  /**
   * Reset notification type
   */
  const resetNotification = useCallback(() => {
    setComparatorNotificationType(null)
  }, [])

  // Auto-load comparator ads on mount if enabled
  useMount(() => {
    if (autoLoad) {
      loadComparatorAds()
    }
  })

  return {
    // State
    comparatorSelectedAds,
    comparatorNotificationType,
    isLoading,

    // Methods
    handleSelectComparar,
    onRemoveSelectedAd,
    clearSelectedAds,
    resetNotification,
    loadComparatorAds,
    trackAdToCompare
  }
}

export default useComparator
